/**
 * Service for fetching and managing dashboard data
 */

/**
 * Custom fetch wrapper for web admin authentication
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise} - API response
 */
const fetchWithWebUserAuth = async (endpoint, options = {}) => {
  const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

  
  // Get the web user token
  // Check multiple possible token names to handle different auth systems
  const webUserToken = 
    localStorage.getItem('userToken_TOVIAPPS');
  
  // Set up headers with auth token
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add auth token if available
  if (webUserToken) {
    headers.Authorization = `Bearer ${webUserToken}`;
  } else {
    console.warn('No authentication token found in storage! Authentication is required for this endpoint.');
    throw new Error('Token de autenticação não encontrado. Por favor, faça login novamente.');
  }
  
  try {
    const fullUrl = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });
        
    // Handle 401 Unauthorized (expired token)
    if (response.status === 401) {
      localStorage.removeItem('userToken_TOVIAPPS'); // Clear the invalid token
      throw new Error('Sua sessão expirou ou o token é inválido. Por favor, faça login novamente.');
    }
    
    // Handle 403 Forbidden (insufficient permissions)
    if (response.status === 403) {
      throw new Error('Você não tem permissão para acessar esta página. Acesso restrito a administradores.');
    }
    
    if (!response.ok) {
      throw new Error(`Falha na requisição da API com status ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    
    // Check for specific JWT error messages
    if (error.message && (
      error.message.includes('invalid signature') || 
      error.message.includes('jwt malformed') || 
      error.message.includes('jwt expired')
    )) {
      // Clear the invalid tokens
      localStorage.removeItem('userToken_TOVIAPPS');
      
      throw new Error('Token de autenticação inválido. Por favor, faça login novamente.');
    }
    
    throw error;
  }
};

/**
 * Fetch dashboard data from the API
 * @param {string} timeFrame - Time frame for data (day, week, month, quarter, year)
 * @returns {Promise} - Promise with dashboard data
 */
export const fetchDashboardData = async (timeFrame) => {  
  try {
    // Make the API request
    const response = await fetchWithWebUserAuth(`api/dashboard/master/stats?timeFrame=${timeFrame}`, {
      method: 'GET'
    });
    
    // Parse the JSON response
    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error);
    throw error;
  }
};
